import ProductVariantComboBox, { ProductVariantItem } from '@/components/ProductVariantComboBox';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useFormContext } from 'react-hook-form';

export interface Sku {
  id: string;
  name: string;
  externalCode?: string;
}

interface PromotionProductFormProps {
  supplierId?: string;
}

interface FormData {
  skus: Sku[];
  skuIds: string[];
}

const PromotionProductForm = ({ supplierId }: PromotionProductFormProps) => {
  const { getValues, setValue, watch } = useFormContext<FormData>();

  const skus = getValues('skus') || [];

  const handleSelectSkus = (
    selected: ProductVariantItem[] | null,
    items?: ProductVariantItem[],
  ) => {
    const currentSkus = getValues('skus') || [];
    const currentIds = getValues('skuIds') || [];

    // Add new selections
    const newSkus = selected ? selected.filter((sku) => !currentIds.includes(sku.id)) : [];

    const addedSkus = newSkus.map((sku) => ({
      id: sku.id,
      name: sku.name,
      externalCode: sku.sku.externalCode,
    }));

    // Remove deselected items that are in the visible items list
    const removedIds = items
      ? items
          .map((sku) => sku.id)
          .filter((id) => (selected ? !selected.some((s) => s.id === id) : true))
      : [];

    const finalSkus = [...currentSkus.filter((sku) => !removedIds.includes(sku.id)), ...addedSkus];

    const finalIds = finalSkus.map((sku) => sku.id);

    // Check if anything changed
    const isSame =
      finalIds.length === currentIds.length && finalIds.every((id) => currentIds.includes(id));
    if (isSame) return;

    setValue('skus', finalSkus, { shouldValidate: true, shouldDirty: true });
    setValue('skuIds', finalIds, { shouldValidate: true, shouldDirty: true });
  };

  const handleRemoveSku = (skuId: string) => {
    setValue(
      'skus',
      getValues('skus').filter((sku) => sku.id !== skuId),
    );
    setValue(
      'skuIds',
      getValues('skuIds').filter((id) => id !== skuId),
    );
  };

  const skuIds = watch('skuIds');

  return (
    <div className="col-span-2 my-4">
      <span className="block font-bold text-xl mb-6 pb-4 border-b border-gray-300">Products</span>

      <div className="flex flex-col gap-4">
        <ProductVariantComboBox
          value={skuIds}
          hideLabel
          supplierId={supplierId}
          onSelect={handleSelectSkus}
          multiple
        />

        {/* Selected SKUs */}
        {skus.length > 0 && (
          <ul className="divide-y divide-gray-200 border rounded-md">
            {skus.map((sku) => (
              <li key={sku.id} className="flex justify-between items-center px-3 py-2 text-sm">
                <div className="flex flex-col">
                  <span className="font-medium">{sku.name}</span>
                  <span className="text-gray-500 text-xs">{sku.externalCode}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveSku(sku.id)}
                  className="text-gray-500 hover:text-red-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default PromotionProductForm;
